import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Building2, Pencil, Check, X, Loader2 } from "lucide-react";

const STATI = {
  verificato: ["Verificato", "text-emerald-600 border-emerald-600/40 bg-emerald-500/10"],
  manuale: ["Confermato a mano", "text-sky-600 border-sky-600/40 bg-sky-500/10"],
  probabile: ["Probabile", "text-amber-600 border-amber-600/40 bg-amber-500/10"],
  da_verificare: ["Da verificare", "text-red-600 border-red-600/40 bg-red-500/10"],
};

function Row({ label, d, k, editing, val, setVal }) {
  const [txt, cls] = STATI[d?.stato] || STATI.da_verificare;
  return (
    <div className="flex flex-wrap items-start gap-3 py-2 border-b border-border last:border-0" data-testid={`fornitura-${k}`}>
      <div className="w-28 text-xs text-muted-foreground pt-0.5">{label}</div>
      <div className="flex-1 min-w-[180px]">
        {editing ? (
          <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder={d?.nome || ""} className="h-8 text-sm" data-testid={`fornitura-input-${k}`} />
        ) : (
          <div className="text-sm font-semibold" data-testid={`fornitura-nome-${k}`}>{d?.nome || "—"}</div>
        )}
        {!editing && d?.letto && d.letto !== d.nome && <div className="text-[11px] text-muted-foreground">Letto in bolletta: {d.letto}</div>}
        {!editing && (d?.motivi || []).length > 0 && <div className="text-[11px] text-muted-foreground">{d.motivi.join(" ")}</div>}
      </div>
      {!editing && <span className={`text-[10px] uppercase tracking-wider border rounded px-1.5 py-0.5 ${cls}`} data-testid={`fornitura-stato-${k}`}>{txt}</span>}
    </div>
  );
}

// Fornitore e distributore letti dalla bolletta, con stato di verifica automatica.
export default function LeadFornitura({ lead, onUpdate }) {
  const f = lead.fornitura;
  const [editing, setEditing] = useState(false);
  const [vf, setVf] = useState("");
  const [vd, setVd] = useState("");
  const [busy, setBusy] = useState(false);
  if (!f) return null;

  const open = () => { setVf(f.fornitore?.nome || ""); setVd(f.distributore?.nome || ""); setEditing(true); };
  const save = async () => {
    setBusy(true);
    try {
      const body = {};
      if (vf.trim() && vf.trim() !== f.fornitore?.nome) body.fornitore = vf;
      if (vd.trim() && vd.trim() !== f.distributore?.nome) body.distributore = vd;
      if (Object.keys(body).length) { const { data } = await api.patch(`/leads/${lead.id}/fornitura`, body); onUpdate?.(data); toast.success("Fornitura aggiornata"); }
      setEditing(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Card className="rounded border-border p-5" data-testid="fornitura-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 font-semibold text-sm"><Building2 className="h-4 w-4 text-primary" /> Fornitura attuale</div>
        {editing ? (
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid="fornitura-cancel"><X className="h-4 w-4" /></Button>
            <Button size="sm" onClick={save} disabled={busy} data-testid="fornitura-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}</Button>
          </div>
        ) : (
          <Button size="sm" variant="ghost" onClick={open} data-testid="fornitura-edit"><Pencil className="h-4 w-4" /></Button>
        )}
      </div>
      <Row label="Fornitore" d={f.fornitore} k="fornitore" editing={editing} val={vf} setVal={setVf} />
      <Row label="Distributore" d={f.distributore} k="distributore" editing={editing} val={vd} setVal={setVd} />
    </Card>
  );
}
